import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import { financialAPI, DashboardData } from '../services/api';

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const theme = useTheme();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await financialAPI.getDashboardData();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Custom tooltip component that respects dark mode
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          boxShadow: theme.shadows[4],
        }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {`${entry.dataKey}: ${formatCurrency(entry.value)}`}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No dashboard data available
      </Alert>
    );
  }

  const summaryCards = [
    {
      title: 'Total Income',
      value: dashboardData.totalIncome,
      icon: <TrendingUpIcon />,
      color: '#4caf50',
    },
    {
      title: 'Total Expenses',
      value: dashboardData.totalExpenses,
      icon: <TrendingDownIcon />,
      color: '#f44336',
    },
    {
      title: 'Monthly Savings',
      value: dashboardData.monthlySavings,
      icon: <SavingsIcon />,
      color: dashboardData.monthlySavings >= 0 ? '#4caf50' : '#f44336',
    },
    {
      title: 'Net Worth',
      value: dashboardData.totalNetWorth,
      icon: <AccountBalanceWalletIcon />,
      color: '#2196f3',
    },
  ];

  return (
    <Box>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          fontSize: { xs: '1.75rem', sm: '2.125rem' },
          textAlign: { xs: 'center', sm: 'left' },
          mb: { xs: 2, sm: 3 },
        }}
      >
        Financial Dashboard
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          sm: '1fr 1fr', 
          md: '1fr 1fr 1fr 1fr' 
        },
        gap: { xs: 2, sm: 3 },
        mb: { xs: 3, sm: 4 },
      }}>
        {summaryCards.map((card, index) => (
          <Card key={index} elevation={3}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    color="textSecondary" 
                    gutterBottom 
                    variant="h6"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 500,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    component="div" 
                    sx={{ 
                      color: card.color,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      fontWeight: 600,
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatCurrency(card.value)}
                  </Typography>
                </Box>
                <Box sx={{ 
                  color: card.color,
                  ml: 1,
                  '& svg': {
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                  },
                }}>
                  {card.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Charts */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: { xs: 2, sm: 3 },
      }}>
        {/* Expense Breakdown Pie Chart */}
        <Card elevation={3}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Monthly Expenses by Category
            </Typography>
            <Box sx={{ 
              width: '100%', 
              height: 350,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                <Pie
                  data={dashboardData.expensesByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                  outerRadius={isMobile ? 70 : 120}
                  fill="#8884d8"
                  dataKey="total"
                  style={{
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: 500,
                  }}
                >
                  {dashboardData.expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0];
                      return (
                        <Box sx={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          p: 1.5,
                          boxShadow: theme.shadows[4],
                        }}>
                          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                            {data.payload.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: data.payload.color }}>
                            {formatCurrency(Number(data.value))}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Income vs Expenses Bar Chart */}
        <Card elevation={3}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              Income vs Expenses
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={[
                  {
                    name: 'This Month',
                    Income: dashboardData.totalIncome,
                    Expenses: dashboardData.totalExpenses,
                    Savings: dashboardData.monthlySavings,
                  },
                ]}
                margin={{ top: 20, right: 15, left: 15, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ 
                    fontSize: '12px',
                    color: theme.palette.text.primary,
                  }} 
                />
                <Bar dataKey="Income" fill="#4caf50" />
                <Bar dataKey="Expenses" fill="#f44336" />
                <Bar dataKey="Savings" fill="#2196f3" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;
